/**
 * Droits côté interface — lecture de la carte renvoyée par /auth/login et /auth/me.
 *
 * Forme de la carte (cf. doc/specs_techniques/api_rest.md) :
 *     user.droits = { "1": { "programme": "modifier", "etudiants": "masque", … } }
 *
 * Trois usages, conformes à la spec Administration §4.3 :
 *   1. la sidebar masque les modules en « masque » ;
 *   2. un module passe en lecture seule si « consulter » ;
 *   3. le backend refuse de toute façon (403) — l'interface ne fait qu'éviter à
 *      l'utilisateur de cliquer sur ce qui sera rejeté.
 *
 * Le masquage n'est PAS une sécurité : la sécurité est le refus serveur.
 */

export const NIVEAU_MASQUE = 'masque';
export const NIVEAU_CONSULTER = 'consulter';
export const NIVEAU_MODIFIER = 'modifier';

/** Département courant : celui de la configuration chargée, sinon le premier rattaché. */
export function departementCourant(user, config) {
    return (
        config?.departement?.id ??
        config?.departementId ??
        user?.departements?.[0]?.id ??
        null
    );
}

/**
 * Niveau de droit sur un module. Renvoie 'modifier' | 'consulter' | 'masque'.
 *
 * Session ouverte avant la mise en place des droits (pas de carte en mémoire) :
 * on ne masque rien, le backend reste seul juge. Le rafraîchissement par
 * /auth/me au démarrage de l'application règle ce cas dès le premier chargement.
 */
export function niveauModule(user, departementId, moduleKey) {
    if (!user) return NIVEAU_MASQUE;
    if (user.is_super_admin) return NIVEAU_MODIFIER;
    if (!user.droits) return NIVEAU_MODIFIER;

    const carte = user.droits[String(departementId)] || user.droits[departementId] || {};
    return carte[moduleKey] || NIVEAU_MASQUE;
}

export function peutConsulter(user, departementId, moduleKey) {
    const niveau = niveauModule(user, departementId, moduleKey);
    return niveau === NIVEAU_MODIFIER || niveau === NIVEAU_CONSULTER;
}

export function peutModifier(user, departementId, moduleKey) {
    return niveauModule(user, departementId, moduleKey) === NIVEAU_MODIFIER;
}

/**
 * Gouvernance à TROIS étages (spec Administration §3, lot CO-a) — notions distinctes,
 * qu'il ne faut pas confondre :
 *
 *   estSuperAdmin           gouverne toute l'application (composantes, années,
 *                           profils globaux, clés d'API, configuration)
 *   estAdminComposante      administre une composante, donc chacun de ses départements,
 *                           sans en être membre (aucun droit sur les modules)
 *   estAdminDepartement     administre un département précis — lui-même, par sa
 *                           composante, ou super-admin
 *   peutAccederAdministration  l'un des trois — c'est la condition d'entrée du panneau
 *
 * ⚠️ Rien n'est recalculé ici : le serveur envoie `user.administre`
 * (services/gouvernance.js, la règle unique) et l'écran le LIT.
 *
 * Le rôle historique `role_nom === 'admin'` n'est PLUS reconnu : depuis 0.B.5 le
 * serveur exige `is_super_admin` ou `role_dept = 'admin'`. Le laisser côté
 * interface ouvrirait un panneau dont toutes les requêtes seraient refusées (403).
 */
export function estSuperAdmin(user) {
    return user?.is_super_admin === true;
}

/** Ce que le serveur dit que l'utilisateur administre (repli : session ouverte avant le lot CO-a). */
function administre(user) {
    if (user?.administre) return user.administre;
    const ids = (user?.departements || []).filter((d) => d.role_dept === 'admin').map((d) => d.id);
    return { composantes: [], departements: ids, par_composante: [] };
}

/** `departementId` omis : administre AU MOINS quelque chose. */
export function estAdminDepartement(user, departementId) {
    if (user?.is_super_admin) return true;
    const a = administre(user);
    if (departementId === undefined || departementId === null) {
        return a.departements.length > 0 || a.composantes.length > 0;
    }
    return a.departements.includes(Number(departementId));
}

/** Administrateur d'au moins une composante (`composanteId` omis) ou de celle-ci. */
export function estAdminComposante(user, composanteId) {
    const a = administre(user);
    if (composanteId === undefined || composanteId === null) return a.composantes.length > 0;
    return a.composantes.includes(Number(composanteId));
}

export function peutAccederAdministration(user) {
    return estSuperAdmin(user) || estAdminDepartement(user);
}

/** Départements que l'utilisateur administre — les siens et ceux de sa composante. */
export function departementsAdministres(user) {
    if (estSuperAdmin(user)) return null; // null = tous
    return administre(user).departements;
}
